import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import useUrgencyDiscountUIEnabled from 'hooks/useUrgencyDiscountUIEnabled';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { trackDiscountUIShown } from 'metrics/user-analytics/search';
import { shouldShowProviderDiscountForSearch } from 'utils/passengerCategories';
import { getMomentFromSearchDateParam } from 'utils/search/searchDateParam';
import getDiscountUrgencyPromoStats, {
  getTripsForDiscountStats,
} from 'utils/urgencyDiscountBanner';

/**
 * Tracks the effective discount UI shown by a filtered result list.
 *
 * @param {Object} params - Tracker params.
 * @param {Array} params.trips - Trips visible in the result list.
 * @param {boolean} params.roundTrip - Whether the search is round trip.
 * @param {string} params.way - Result direction.
 * @param {Object} params.analyticsSearchData - Normalized search analytics data.
 * @param {boolean} params.searchIsPolling - Whether the search is polling.
 * @param {string} params.searchDate - Date represented by the list.
 * @param {Object|null} params.departureTrip - Selected departure trip for return validation.
 * @param {string} params.scopeId - Provider scope represented by the list.
 * @returns {void}
 */
const useTrackDiscountUIShown = ({
  trips,
  roundTrip,
  way,
  analyticsSearchData,
  searchIsPolling,
  searchDate,
  departureTrip,
  scopeId,
}) => {
  const trackedImpressionKey = useRef(null);
  const urgencyDiscountUIEnabled = useUrgencyDiscountUIEnabled();
  const searchPassengersState = useSelector((state) => state.search.get('passengers'));
  const searchPassengers = useMemo(
    () => searchPassengersState?.toJS() || {},
    [searchPassengersState],
  );
  const features = useWhitelabelFeatures() || {};
  const categoryBasedPricing = useGrowthBookFeatureValue('category_based_pricing');
  const halfPriceCategoryFallback = useGrowthBookFeatureValue('half_price_category_fallback');
  const validateRoundTripDiscount = Boolean(features.VALIDATE_ROUND_TRIP_DISCOUNT);
  const stats = useMemo(() => {
    const visibleDiscountTrips = trips.filter(
      (trip) =>
        trip &&
        shouldShowProviderDiscountForSearch(
          searchPassengers,
          Array.isArray(trip.passengerTypes) ? trip.passengerTypes : [],
          Boolean(categoryBasedPricing),
          Boolean(halfPriceCategoryFallback),
        ),
    );
    const trackableTrips = getTripsForDiscountStats({
      trips: visibleDiscountTrips,
      way,
      validateRoundTripDiscount,
      departureTrip,
    });

    return getDiscountUrgencyPromoStats({ trips: trackableTrips, roundTrip, way });
  }, [
    categoryBasedPricing,
    departureTrip,
    halfPriceCategoryFallback,
    roundTrip,
    searchPassengers,
    trips,
    validateRoundTripDiscount,
    way,
  ]);
  const discountUI = urgencyDiscountUIEnabled ? 'new' : 'classic';
  const discountedResultCount = urgencyDiscountUIEnabled
    ? stats.resultDiscountCount
    : stats.scheduleCount + (features.SHOW_DIGITAL_DISCOUNT_BADGE ? stats.digitalDiscountCount : 0);
  const currentSearchDate = searchDate
    ? getMomentFromSearchDateParam(searchDate).startOf('day').format()
    : null;

  useEffect(() => {
    if (discountedResultCount === 0 || searchIsPolling || !analyticsSearchData) return;

    const impressionKey = [
      analyticsSearchData.id,
      stats.currentRoute,
      currentSearchDate,
      way,
      scopeId,
      discountUI,
    ].join('|');

    if (trackedImpressionKey.current === impressionKey) return;

    trackedImpressionKey.current = impressionKey;
    trackDiscountUIShown(analyticsSearchData, {
      ...stats,
      currentDate: currentSearchDate,
      discountedResultCount,
      discountUI,
      way,
    });
  }, [
    analyticsSearchData,
    currentSearchDate,
    discountedResultCount,
    discountUI,
    scopeId,
    searchIsPolling,
    stats,
    way,
  ]);
};

export default useTrackDiscountUIShown;
